/**
 * Instellingen → AI (super-admin): hoe de installatie zich bij de AI-aanbieder
 * aanmeldt (zelfstandig-installeren.md §4.2). Eén instelling voor alle
 * workspaces.
 *
 * - Workload Identity Federation: de admin neemt issuer, JWKS-URL en subject
 *   over in de Claude Console en vult daarna de vier ID's in.
 * - Een API-sleutel: versleuteld opgeslagen, daarna alleen de laatste vier
 *   tekens zichtbaar.
 *
 * Staan beide ingesteld, dan gebruikt de worker WIF en valt hij bij een
 * storing terug op de sleutel.
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AiAuthStatusDTO, ApiError, api } from "../api/client";
import { ld, pill } from "./ledgerUi";

const inputClass = `${ld.input} font-mono`;

export function AiAuthSettings() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AiAuthStatusDTO | null>(null);
  const [wif, setWif] = useState({
    organization_id: "",
    service_account_id: "",
    federation_rule_id: "",
    anthropic_workspace_id: "",
  });
  const [keyDraft, setKeyDraft] = useState("");
  /** Alleen nodig bij een sleutel die niet aan één Anthropic-workspace hangt. */
  const [keyWorkspace, setKeyWorkspace] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const apply = useCallback((s: AiAuthStatusDTO) => {
    setStatus((prev) => ({
      ...s,
      federation: s.federation ?? prev?.federation,
      providers: s.providers ?? prev?.providers,
    }));
    setWif({
      organization_id: s.wif?.organization_id ?? "",
      service_account_id: s.wif?.service_account_id ?? "",
      federation_rule_id: s.wif?.federation_rule_id ?? "",
      anthropic_workspace_id: s.wif?.anthropic_workspace_id ?? "",
    });
    setKeyWorkspace(s.api_key?.anthropic_workspace_id ?? "");
  }, []);

  useEffect(() => {
    api
      .getAiAuth()
      .then(apply)
      .catch((err) => setError(explain(err)));
  }, [apply]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  const saveWif = () =>
    run(async () => {
      apply(
        await api.putAiAuth({
          wif: {
            organization_id: wif.organization_id.trim(),
            service_account_id: wif.service_account_id.trim(),
            federation_rule_id: wif.federation_rule_id.trim(),
            anthropic_workspace_id: wif.anthropic_workspace_id.trim() || null,
          },
        }),
      );
      setInfo(t("settings.aiAuth.saved"));
    });

  const clearWif = () =>
    run(async () => {
      apply(await api.putAiAuth({ wif: null }));
      setInfo(t("settings.aiAuth.saved"));
    });

  const saveKey = () =>
    run(async () => {
      apply(
        await api.putAiAuth({
          ...(keyDraft.trim() ? { api_key: keyDraft.trim() } : {}),
          api_key_workspace_id: keyWorkspace.trim() || null,
        }),
      );
      setKeyDraft("");
      setInfo(t("settings.aiAuth.saved"));
    });

  const clearKey = () =>
    run(async () => {
      apply(await api.putAiAuth({ api_key: null }));
      setInfo(t("settings.aiAuth.saved"));
    });

  const test = () =>
    run(async () => {
      const res = await api.testAiAuth();
      const mode =
        res.mode === "wif"
          ? t("settings.aiAuth.modeWif")
          : res.mode === "dev_key"
            ? t("settings.aiAuth.modeDevKey")
            : t("settings.aiAuth.modeKey");
      if (res.ok) setInfo(t("settings.aiAuth.testOk", { mode }));
      else
        setError(
          t("settings.aiAuth.testFailed", {
            detail: res.detail ?? (res.status ? `HTTP ${res.status}` : "—"),
          }),
        );
      apply(await api.getAiAuth());
    });

  const federation = status?.federation;
  const wifComplete =
    wif.organization_id.trim() && wif.service_account_id.trim() && wif.federation_rule_id.trim();
  const anythingSet = !!status?.wif || !!status?.api_key;

  return (
    <div className="flex flex-col gap-6">
      <p className={ld.muted}>
        {t("settings.aiAuth.provider", {
          name:
            status?.providers?.find((p) => p.id === status.provider)?.display_name ??
            "Anthropic (Claude)",
        })}{" "}
        {t("settings.aiAuth.providerFuture")}
      </p>

      <section className={ld.panel}>
        <div className="flex items-center gap-2">
          <h4 className={ld.sectionTitle}>{t("settings.aiAuth.wifTitle")}</h4>
          {status?.wif && <span className={pill.positive}>{t("settings.aiAuth.wifSet")}</span>}
        </div>
        {federation && !federation.available ? (
          <p className={`mt-3 ${ld.notice}`}>{t("settings.aiAuth.wifUnavailable")}</p>
        ) : (
          <p className={ld.sectionIntro}>{t("settings.aiAuth.wifIntro")}</p>
        )}
        {federation && (
          <dl className="mt-4 grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 rounded-md bg-(--ld-raised) px-3 py-2.5 text-sm">
            <CopyRow label={t("settings.aiAuth.issuer")} value={federation.issuer} />
            <CopyRow label={t("settings.aiAuth.jwksUrl")} value={federation.jwks_url} />
            <CopyRow label={t("settings.aiAuth.subject")} value={federation.subject} />
          </dl>
        )}
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {(
            [
              ["organization_id", "organizationId"],
              ["service_account_id", "serviceAccountId"],
              ["federation_rule_id", "federationRuleId"],
              ["anthropic_workspace_id", "anthropicWorkspaceId"],
            ] as const
          ).map(([field, label]) => (
            <div key={field}>
              <label htmlFor={`ai-wif-${field}`} className={ld.label}>
                {t(`settings.aiAuth.${label}`)}
              </label>
              <input
                id={`ai-wif-${field}`}
                className={inputClass}
                value={wif[field]}
                onChange={(e) => setWif((w) => ({ ...w, [field]: e.target.value }))}
                autoComplete="off"
                spellCheck={false}
                disabled={busy}
                {...(field === "anthropic_workspace_id"
                  ? { "aria-describedby": "ai-wif-workspace-hint" }
                  : {})}
              />
              {field === "anthropic_workspace_id" && (
                <p id="ai-wif-workspace-hint" className={ld.hint}>
                  {t("settings.aiAuth.anthropicWorkspaceHint")}
                </p>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className={ld.primary}
            disabled={busy || !wifComplete}
            onClick={() => void saveWif()}
          >
            {t("settings.aiAuth.saveWif")}
          </button>
          <button
            type="button"
            className={ld.danger}
            disabled={busy || !status?.wif}
            onClick={() => void clearWif()}
          >
            {t("settings.aiAuth.clearWif")}
          </button>
        </div>
      </section>

      <section className={ld.panel}>
        <div className="flex items-center gap-2">
          <h4 className={ld.sectionTitle}>{t("settings.aiAuth.keyTitle")}</h4>
          {status?.api_key ? (
            <span className={pill.positive}>
              {t("settings.aiAuth.keySet", { last4: status.api_key.last4 })}
            </span>
          ) : (
            <span className={pill.neutral}>{t("settings.aiAuth.keyUnset")}</span>
          )}
        </div>
        <p className={ld.sectionIntro}>{t("settings.aiAuth.keyIntro")}</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="ai-key-new" className={ld.label}>
              {t("settings.aiAuth.newKey")}
            </label>
            <input
              id="ai-key-new"
              className={inputClass}
              type="password"
              value={keyDraft}
              onChange={(e) => setKeyDraft(e.target.value)}
              placeholder={t("settings.aiAuth.keyPlaceholder")}
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
          </div>
          <div>
            <label htmlFor="ai-key-workspace" className={ld.label}>
              {t("settings.aiAuth.keyWorkspace")}
            </label>
            <input
              id="ai-key-workspace"
              aria-describedby="ai-key-workspace-hint"
              className={inputClass}
              value={keyWorkspace}
              onChange={(e) => setKeyWorkspace(e.target.value)}
              placeholder="wrkspc_…"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
            />
            <p id="ai-key-workspace-hint" className={ld.hint}>
              {t("settings.aiAuth.keyWorkspaceHelp")}
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            className={ld.primary}
            disabled={
              busy ||
              (!keyDraft.trim() &&
                (!status?.api_key ||
                  keyWorkspace.trim() === (status.api_key.anthropic_workspace_id ?? "")))
            }
            onClick={() => void saveKey()}
          >
            {t("settings.aiAuth.saveKey")}
          </button>
          <button
            type="button"
            className={ld.danger}
            disabled={busy || !status?.api_key}
            onClick={() => void clearKey()}
          >
            {t("settings.aiAuth.clearKey")}
          </button>
        </div>
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={ld.secondary}
          disabled={busy || !anythingSet}
          onClick={() => void test()}
        >
          {busy ? t("settings.aiAuth.testing") : t("settings.aiAuth.test")}
        </button>
        {status?.last_failure_at && (
          <span className="text-(--ld-notice-ink) text-[13px]">
            {t("settings.aiAuth.lastFailure", {
              date: new Date(status.last_failure_at).toLocaleString(),
              reason: status.last_failure_reason ?? "—",
            })}
          </span>
        )}
      </div>

      {error && (
        <p role="alert" className={ld.error}>
          {error}
        </p>
      )}
      {info && (
        <p role="status" className={ld.success}>
          {info}
        </p>
      )}
    </div>
  );
}

function CopyRow({ label, value }: { label: string; value: string | null }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  return (
    <>
      <dt className="text-(--ld-muted)">{label}</dt>
      <dd className="min-w-0 truncate font-mono text-xs" title={value ?? ""}>
        {value ?? "—"}
      </dd>
      <dd>
        <button
          type="button"
          className={ld.textButton}
          disabled={!value}
          onClick={() => {
            if (!value) return;
            void navigator.clipboard?.writeText(value).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
        >
          {copied ? t("settings.aiAuth.copied") : t("settings.aiAuth.copy")}
        </button>
      </dd>
    </>
  );
}

function explain(err: unknown): string {
  if (err instanceof ApiError) return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  if (err instanceof Error) return err.message;
  return String(err);
}
